import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectFormOptions } from 'src/store/selectors/formOptions';
import {
  IContactFormDataForAPI,
  IContactPayloadForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import { toast } from 'react-toastify';
import { addExistingPropertyDeveloper } from 'src/services/client';
import {
  handleImageUpload,
  transformContactFormDataForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { buildPropertiesContactsPayload } from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/helpers';
import {
  useContactAPI,
  UseContactAPIReturn,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';

export interface UseContactAPIOperationsPropertiesOptions {
  propertyId: string;
  adminContactId?: string;
  editingExistingContactInTable?: boolean;
  appendingNewContactToExistingProperty?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
  onRefreshDevelopers?: (propertyId: any) => Promise<void>;
}

export interface UseContactAPIOperationsPropertiesReturn {
  isLoading: boolean;
  response: any;
  error: string | null;
  clearResponse: () => void;
  handleSubmitContactForm: (
    formData: IContactFormData,
    formType: string
  ) => Promise<void>;
}

/* Write-side orchestrator for property-level contacts (Property Developers,
 * Service Managers). Composes useContactAPI + buildPropertiesContactsPayload
 * and owns the submit dispatch. Read/delete live in
 * usePropertyDevelopersListOperations so the parent page and child table each
 * instantiate only what they use.
 *
 * ⚠️ On Property Developer edits, buildPropertiesContactsPayload deliberately
 * strips the `Properties` and `Suites` arrays from the request payload — see
 * `stripFieldsThatOrphanOnUpdate` in ./helpers.ts for the fragile backend
 * contract (EF RemoveRange on PUT /api/contacts/Users) that forces this. */
export const useContactAPIOperationsProperties = ({
  propertyId,
  adminContactId,
  editingExistingContactInTable = false,
  appendingNewContactToExistingProperty = false,
  onSuccess,
  onClose,
  onRefreshDevelopers,
}: UseContactAPIOperationsPropertiesOptions): UseContactAPIOperationsPropertiesReturn => {
  const contactAPI: UseContactAPIReturn = useContactAPI();

  const adminUser = useSelector(selectAuthenticatedUser);

  /* TODO: migrate allRoles off Redux formOptions onto useUserManagementAPI,
   * matching the Contacts / Suites orchestrators. Deferred — out of scope
   * for WEB-1325 (email self-service toggle only touches Contacts + Suites flows). */
  const formOptions = useSelector(selectFormOptions);

  const createContact = async (
    formData: IContactFormData
  ): Promise<boolean> => {
    try {
      const imageUrl: string = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);

      const payload: IContactPayloadForAPI = buildPropertiesContactsPayload(
        apiFormData,
        propertyId,
        false,
        appendingNewContactToExistingProperty,
        formOptions?.allRoles,
        adminUser.id
      );

      const { ok } = await contactAPI.saveContact(payload);
      return ok;
    } catch (err: any) {
      console.error('Contact creation failed:', err);
      return false;
    }
  };

  const editContact = async (formData: IContactFormData): Promise<boolean> => {
    if (!formData.id) {
      const error = 'Cannot edit contact: Missing contact ID';
      console.error(error);

      toast.error(`🚫 ${error}`, {
        autoClose: 8000,
        position: 'top-center',
      });

      return false;
    }

    try {
      const imageUrl: string = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);

      /* Payload builder strips Properties/Suites on PD edits — see
       * stripFieldsThatOrphanOnUpdate in ./helpers.ts. */
      const payload: IContactPayloadForAPI = buildPropertiesContactsPayload(
        apiFormData,
        propertyId,
        editingExistingContactInTable,
        appendingNewContactToExistingProperty,
        formOptions?.allRoles,
        adminUser.id
      );

      const { ok } = await contactAPI.updateContact(payload);
      return ok;
    } catch (err: any) {
      console.error('Contact update failed:', err);
      return false;
    }
  };

  const linkExistingContactToProperty = async (
    formData: IContactFormData
  ): Promise<boolean> => {
    if (!adminContactId) {
      console.error('adminContactId is required for adding existing users');
      return false;
    }

    const requestPayload = {
      PropertyId: propertyId,
      PropertyManagerId: formData.id,
      UserId: adminContactId,
      Role: formData.userRole,
      IsPrimaryContact: formData.isPrimaryContact,
    };

    try {
      const response: any = await addExistingPropertyDeveloper(requestPayload);

      if (response.status === 200 || response.ok) {
        const responseJson = await response.json();

        if (responseJson.statusCode === 200) {
          toast.success('ⓘ User added to property successfully!', {
            autoClose: 10000,
            position: 'top-center',
          });
          return true;
        } else {
          throw new Error(
            `API returned status code: ${responseJson.statusCode}`
          );
        }
      } else {
        throw new Error(`Request failed with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Adding existing user to property failed:', err);
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      return false;
    }
  };

  const handleSubmitContactForm = async (
    formData: IContactFormData,
    formType: string
  ): Promise<void> => {
    const isLinkingExistingContactToProperty: boolean = formType === 'Existing';
    const isEditingExistingContact: boolean =
      !isLinkingExistingContactToProperty && Boolean(formData.id);

    try {
      let submissionSuccess: boolean;

      if (isLinkingExistingContactToProperty) {
        submissionSuccess = await linkExistingContactToProperty(formData);
      } else if (isEditingExistingContact) {
        submissionSuccess = await editContact(formData);
      } else {
        submissionSuccess = await createContact(formData);
      }

      if (submissionSuccess) {
        onSuccess?.();

        if (onRefreshDevelopers) {
          await onRefreshDevelopers(propertyId);
        }

        onClose?.();
      }
    } catch (error) {
      console.error('Contact form submission failed:', error);
    }
  };

  return {
    isLoading: contactAPI.isSaving,
    response: contactAPI.lastResponse,
    error: contactAPI.error,
    clearResponse: contactAPI.clearResponse,
    handleSubmitContactForm,
  };
};
